import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, CheckCircle, XCircle } from 'lucide-react';

export default function EmailConfiguration() {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;
  
  const [config, setConfig] = useState({
    smtp_server: '',
    smtp_port: '587',
    username: '',
    password: '',
    sender_name: 'FOPAG Place',
    sender_email: 'noreply@fopag.com',
    use_tls: true,
    use_ssl: false
  });
  
  const [testEmail, setTestEmail] = useState('teste@exemplo.com');
  const [status, setStatus] = useState({
    server: 'not_configured',
    authentication: 'not_configured', 
    security: 'not_configured'
  });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchEmailConfig();
  }, []);

  const fetchEmailConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/email-config`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.smtp_server) {
          setConfig(data);
          updateStatus(data);
        }
      }
    } catch (error) {
      console.error('Error fetching email config:', error);
    }
  };

  const updateStatus = (configData) => {
    const newStatus = {
      server: configData.smtp_server ? 'configured' : 'not_configured',
      authentication: (configData.username && configData.password) ? 'configured' : 'not_configured',
      security: configData.use_tls ? 'TLS/SSL' : 'not_configured'
    };
    setStatus(newStatus);
  };

  const handleQuickConfig = (provider) => {
    const configs = {
      gmail: {
        smtp_server: 'smtp.gmail.com',
        smtp_port: '587',
        use_tls: true,
        use_ssl: false
      },
      outlook: {
        smtp_server: 'smtp-mail.outlook.com',
        smtp_port: '587',
        use_tls: true,
        use_ssl: false
      },
      yahoo: {
        smtp_server: 'smtp.mail.yahoo.com',
        smtp_port: '587',
        use_tls: true,
        use_ssl: false
      }
    };
    
    setConfig(prev => ({
      ...prev,
      ...configs[provider]
    }));
    
    toast.success(`Configuração ${provider.charAt(0).toUpperCase() + provider.slice(1)} aplicada!`);
  };

  const saveConfiguration = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/email-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        toast.success('Configuração salva com sucesso!');
        updateStatus(config);
      } else {
        throw new Error('Erro ao salvar configuração');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Erro ao salvar configuração');
    } finally {
      setLoading(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Digite um e-mail para teste');
      return;
    }

    setTesting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/email-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          test_email: testEmail,
          config: config
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success('E-mail de teste enviado com sucesso!');
      } else {
        throw new Error(data.detail || 'Erro ao enviar e-mail de teste');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(error.message || 'Erro ao enviar e-mail de teste');
    } finally {
      setTesting(false);
    }
  };

  const getStatusColor = (statusValue) => {
    switch (statusValue) {
      case 'configured':
        return 'bg-green-500';
      case 'TLS/SSL':
        return 'bg-green-500';
      default:
        return 'bg-gray-400';
    }
  };

  const getStatusText = (statusValue) => {
    switch (statusValue) {
      case 'configured':
        return 'Configurado';
      case 'TLS/SSL':
        return 'TLS/SSL';
      default:
        return 'Não configurado';
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <Mail className="w-8 h-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Configuração de E-mail</h1>
        </div>
        <p className="text-gray-600">Configure o servidor SMTP para envio de e-mails de assinatura eletrônica</p>
        
        {/* Success notification */}
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-green-700 text-sm font-medium">Sucesso</span>
          </div>
          <p className="text-green-600 text-sm mt-1">Sistema carregado com sucesso</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Configuration */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* SMTP Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <span>⚙️</span>
                <span>Configurações SMTP</span>
              </CardTitle>
              <p className="text-sm text-gray-600">Configure o servidor de e-mail para envio automático</p>
            </CardHeader>
            <CardContent className="space-y-6">
              
              {/* Quick Configurations */}
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-3 block">
                  Configurações Rápidas
                </Label>
                <div className="flex space-x-3">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleQuickConfig('gmail')}
                  >
                    Gmail
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleQuickConfig('outlook')}
                  >
                    Outlook
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleQuickConfig('yahoo')}
                  >
                    Yahoo
                  </Button>
                </div>
              </div>

              {/* Server and Port */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="smtp_server">
                    Servidor SMTP <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="smtp_server"
                    placeholder="smtp.gmail.com"
                    value={config.smtp_server}
                    onChange={(e) => setConfig({...config, smtp_server: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="smtp_port">
                    Porta <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="smtp_port"
                    type="number"
                    placeholder="587"
                    value={config.smtp_port}
                    onChange={(e) => setConfig({...config, smtp_port: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Authentication */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="username">
                    Usuário/E-mail <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="username"
                    type="email"
                    placeholder="fernando.tavares@placelogistics.com"
                    value={config.username}
                    onChange={(e) => setConfig({...config, username: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="password">
                    Senha/Token <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative mt-1">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={config.password}
                      onChange={(e) => setConfig({...config, password: e.target.value})}
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Sender Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="sender_name">Nome do Remetente</Label>
                  <Input
                    id="sender_name"
                    placeholder="FOPAG Place"
                    value={config.sender_name}
                    onChange={(e) => setConfig({...config, sender_name: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="sender_email">E-mail do Remetente</Label>
                  <Input
                    id="sender_email"
                    type="email"
                    placeholder="noreply@fopag.com"
                    value={config.sender_email}
                    onChange={(e) => setConfig({...config, sender_email: e.target.value})}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    💡 <strong>Dica:</strong> Para evitar erros, use o mesmo e-mail da autenticação
                  </p>
                </div>
              </div>

              {/* Security Options */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Label htmlFor="use_tls">Usar TLS</Label>
                  </div>
                  <Switch
                    id="use_tls"
                    checked={config.use_tls}
                    onCheckedChange={(checked) => setConfig({...config, use_tls: checked})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Label htmlFor="use_ssl">Usar SSL</Label>
                  </div>
                  <Switch
                    id="use_ssl"
                    checked={config.use_ssl}
                    onCheckedChange={(checked) => setConfig({...config, use_ssl: checked})}
                  />
                </div>
              </div>

              {/* Save Button */}
              <Button 
                onClick={saveConfiguration}
                disabled={loading}
                className="w-full bg-gray-800 hover:bg-gray-900"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <span className="mr-2">💾</span>
                    Salvar Configuração
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          
          {/* Email Test */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <span>📧</span>
                <span>Teste de E-mail</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="test_email">E-mail para teste</Label>
                <Input
                  id="test_email"
                  type="email"
                  placeholder="teste@exemplo.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="mt-1"
                />
              </div>
              <Button 
                onClick={sendTestEmail}
                disabled={testing || !config.smtp_server}
                className="w-full"
                variant="outline"
              >
                {testing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                    Enviando...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Enviar Teste
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Status */}
          <Card>
            <CardHeader>
              <CardTitle>Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Servidor</span>
                <Badge className={`text-white text-xs ${getStatusColor(status.server)}`}>
                  {getStatusText(status.server)}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Autenticação</span>
                <Badge className={`text-white text-xs ${getStatusColor(status.authentication)}`}>
                  {getStatusText(status.authentication)}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Segurança</span>
                <Badge className={`text-white text-xs ${getStatusColor(status.security)}`}>
                  {getStatusText(status.security)}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Help - Gmail */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <span>💡</span>
                <span>Ajuda - Gmail</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-gray-600 space-y-2">
              <p className="font-medium">Para usar Gmail:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Ative autenticação de 2 fatores</li>
                <li>Gere uma "Senha de app"</li>
                <li>Use a senha de app no campo senha</li>
                <li>Configure TLS na porta 587</li>
              </ol>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}